/**
 * Бенчмарки по нишам, рынок Казахстан.
 * Источник: marketdir_niche_research_kz.xlsx (лист «Бенчмарки»).
 *
 * ВАЖНО: официальной гранулярной статистики по большинству нишевых метрик
 * в РК не существует. Значения с `expert: true` — экспертная оценка,
 * она честно помечается в интерфейсе и калибруется по мере накопления
 * реальных диагностик.
 */

/**
 * Штраф за то, что рычаги на практике мешают друг другу
 * при одновременном исполнении. Настраиваемый параметр, диапазон 0,6–0,7.
 */
export const COMBINATION_FACTOR = 0.65;

/**
 * Потолки реалистичного прироста по каждому рычагу, доля от текущего значения.
 * Абсолютных бенчмарков по РК почти нет (чек и маржа зависят от категории),
 * поэтому запас роста задан относительным приростом — это и есть
 * «верхний квартиль минус текущее значение» в применимой форме.
 *
 * l — поток обращений, c — конверсия, p — средний чек, q — частота покупок.
 */
const DEFAULT_LEVER_CAPS = { l: 0.8, c: 0.4, p: 0.3, q: 0.3 };

export const NICHE_BENCHMARKS = {
  retail_offline: {
    leverCaps: { l: 1.0, c: 0.3, p: 0.35, q: 0.3 },
    conversion: { min: 20, typical: 30, top: 40, unit: '%', expert: true, note: 'посетитель → покупатель' },
    cpl: { min: 1000, typical: 2500, top: 5000, unit: '₸', expert: true },
    source: 'CPC/CPM — Metrium.kz, 2026. Конверсия и глубина чека — экспертная оценка',
    keyMetricBenchmark: { min: 1.5, typical: 2, top: 3, unit: 'позиций', expert: true },
  },
  ecommerce: {
    leverCaps: { l: 1.2, c: 0.6, p: 0.25, q: 0.35 },
    conversion: { min: 1, typical: 2, top: 3, unit: '%', expert: false, note: 'глобальный ориентир, не KZ' },
    cpl: { min: 700, typical: 2000, top: 5000, unit: '₸', expert: false, note: 'TikTok 700–1 500 ₸, Instagram 1 500–5 000 ₸' },
    source: 'Metrium.kz 2026 (CPC/CPM/CPL); Baymard Institute (брошенные корзины, глобально)',
    keyMetricBenchmark: { min: 1, typical: 2, top: 3, unit: '%', expert: false },
  },
  horeca: {
    leverCaps: { l: 0.9, c: 0.25, p: 0.3, q: 0.4 },
    conversion: { min: 20, typical: 35, top: 50, unit: '%', expert: true },
    avgCheck: { min: 1310, typical: 5000, top: 10500, unit: '₸', expert: false, note: 'кофе 1 310 ₸; готовая еда 7 500–10 500 ₸' },
    margin: { min: 15, typical: 20, top: 25, unit: '%', expert: true },
    source: 'Finratings.kz 2025 (чек на кофе); Informburo.kz (рынок готовой еды); маржа — экспертная оценка',
    keyMetricBenchmark: { min: 10, typical: 25, top: 40, unit: '%', expert: true, note: 'доля агрегаторов в выручке' },
  },
  beauty: {
    leverCaps: { l: 0.8, c: 0.35, p: 0.35, q: 0.45 },
    conversion: { min: 25, typical: 40, top: 60, unit: '%', expert: true },
    avgCheck: { min: 6000, typical: 8700, top: 15000, unit: '₸', expert: false, note: 'данные 2022 г., к 2026 выше' },
    adBudget: { min: 100000, typical: 150000, top: 200000, unit: '₸/мес', expert: false },
    source: 'Moluch.ru (рынок салонов KZ, 2022); Metrium.kz 2026 (бюджет таргета)',
    keyMetricBenchmark: { min: 50, typical: 70, top: 85, unit: '%', expert: true, note: 'заполняемость записи' },
  },
  medical: {
    leverCaps: { l: 0.7, c: 0.45, p: 0.5, q: 0.35 },
    conversion: { min: 20, typical: 35, top: 55, unit: '%', expert: true },
    cpl: { min: 500, typical: 2500, top: 50000, unit: '₸', expert: false, note: 'простые услуги 500–2 500 ₸, импланты 10 000–50 000 ₸' },
    avgCheck: { min: 30000, typical: 150000, top: 400000, unit: '₸', expert: true, note: 'курс лечения, иллюстративно' },
    source: 'vc.ru «Лидогенерация для стоматологии» (KZ CPL); чек — отраслевой кейс, не статистика',
    keyMetricBenchmark: { min: 25, typical: 40, top: 60, unit: '%', expert: true, note: 'приём → план лечения' },
  },
  education: {
    leverCaps: { l: 1.1, c: 0.5, p: 0.3, q: 0.25 },
    conversion: { min: 5, typical: 10, top: 20, unit: '%', expert: false, note: 'открытый урок → покупка, общий рынок' },
    avgCheck: { min: 5000, typical: 45000, top: 150000, unit: '₸', expert: false },
    source: 'OSN.kz (EdTech KZ, >45 млрд ₸ в 2025); vc.ru кейсы (конверсия, не KZ-специфично)',
    keyMetricBenchmark: { min: 20, typical: 40, top: 65, unit: '%', expert: true, note: 'доходимость курса' },
  },
  b2b: {
    leverCaps: { l: 0.6, c: 0.4, p: 0.4, q: 0.25 },
    conversion: { min: 10, typical: 20, top: 35, unit: '%', expert: true },
    cycleDays: { min: 14, typical: 30, top: 90, unit: 'дн', expert: true },
    source: 'Metrium.kz (обзор B2B-маркетинга KZ); данных по рынку в целом не найдено',
    keyMetricBenchmark: { min: 14, typical: 30, top: 90, unit: 'дн', expert: true, note: 'цикл сделки' },
  },
  construction: {
    leverCaps: { l: 0.7, c: 0.5, p: 0.3, q: 0.15 },
    conversion: { min: 15, typical: 30, top: 50, unit: '%', expert: true, note: 'смета → договор' },
    avgCheck: { min: 1800000, typical: 4000000, top: 8000000, unit: '₸', expert: false, note: 'ремонт под ключ 45 000–100 000 ₸/м²' },
    source: 'Kromvel.kz, Barsha.kz (цены на ремонт, KZ, 2026); конверсия — экспертная оценка',
    keyMetricBenchmark: { min: 15, typical: 30, top: 50, unit: '%', expert: true },
  },
  realestate: {
    leverCaps: { l: 0.8, c: 0.45, p: 0.25, q: 0.15 },
    conversion: { min: 5, typical: 15, top: 30, unit: '%', expert: true, note: 'показ → сделка' },
    avgCheck: { min: 300000, typical: 400000, top: 500000, unit: '₸', expert: false, note: 'комиссия 1–5% или фикс 300–500 тыс. ₸' },
    cycleDays: { min: 30, typical: 60, top: 90, unit: 'дн', expert: true },
    source: 'Inform.kz, Lada.kz (комиссия риелторов, KZ, 2025–2026)',
    keyMetricBenchmark: { min: 5, typical: 15, top: 30, unit: '%', expert: true },
  },
  auto: {
    leverCaps: { l: 0.7, c: 0.3, p: 0.3, q: 0.5 },
    conversion: { min: 30, typical: 50, top: 70, unit: '%', expert: true },
    avgCheck: { min: 29250, typical: 33688, top: 40000, unit: '₸', expert: false, note: 'средний чек по РК, 2024' },
    source: 'Inform.kz, Autero.kz (средний чек на ремонт авто, KZ, 2024)',
    keyMetricBenchmark: { min: 1, typical: 2, top: 4, unit: 'раз в год', expert: true, note: 'частота визитов' },
  },
};

export const getNicheBenchmarks = (nicheId) => NICHE_BENCHMARKS[nicheId] || null;

export const getLeverCaps = (nicheId) => {
  const benchmarks = getNicheBenchmarks(nicheId);
  return benchmarks ? benchmarks.leverCaps : DEFAULT_LEVER_CAPS;
};

export const CEILING_DISCLAIMER =
  'Это оценочный потолок по модели на основе данных вашей ниши, а не гарантия результата. ' +
  'Точность растёт по мере того, как вы вводите больше собственных фактических данных.';

export const BENCHMARK_NOTE =
  'Значения без указанного года — данные 2025–2026 гг. «Экспертная оценка» означает, что ' +
  'официальной статистики по Казахстану не найдено.';
